import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  MessageCircleQuestion,
  Loader2,
  Send,
  AlertTriangle,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';

/**
 * Phase 4 — CFO What-If Q&A.
 *
 * Read-only chat that asks the deterministic planner to re-run with the
 * given adjustments and lets the AI narrate the resulting diff. The AI
 * cannot write balances, apply moves, or persist anything — every dollar
 * figure originates from the server's deterministic diff payload.
 *
 * Suggested-prompt chips ship structured `adjustments` along with the
 * question so the planner has unambiguous inputs.  Free-text questions
 * are still allowed (with empty adjustments) — in that mode the AI
 * comments on the baseline plan only.
 */

const SUGGESTIONS = [
  {
    id: 'bonus-lower',
    label: 'What if my bonus is $5,000 lower?',
    question: 'What if my upcoming bonus is $5,000 lower than expected?',
    adjustments: { bonusDelta: -5000 },
  },
  {
    id: 'hysa-target',
    label: 'How do I keep $20,000 in HYSA?',
    question: 'What does my plan look like if I want to keep $20,000 in HYSA?',
    adjustments: { hysaTargetOverride: 20000 },
  },
  {
    id: 'pay-amex-full',
    label: 'Can I pay Amex in full?',
    question: 'Can I safely pay my Amex card in full this cycle?',
    adjustments: { payInFullIds: ['amex'] },
  },
  {
    id: 'do-this-week',
    label: 'What should I do this week?',
    question: 'Given my current plan, what should I prioritize this week?',
    adjustments: {},
  },
];

const fmt = (v) => {
  const n = Number(v || 0);
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

function DetChangesBlock({ changes }) {
  if (!changes) return null;
  const rows = [];
  if (changes.status_before && changes.status_after && changes.status_before !== changes.status_after) {
    rows.push(['Plan status', `${changes.status_before} → ${changes.status_after}`]);
  }
  if (changes.hysa_gap_delta) rows.push(['HYSA gap', fmt(changes.hysa_gap_delta)]);
  if (changes.projected_checking_delta) {
    rows.push(['Projected checking', fmt(changes.projected_checking_delta)]);
  }
  if (changes.required_bills_delta) {
    rows.push(['Required bills', fmt(changes.required_bills_delta)]);
  }
  if (!rows.length && !changes.actions_added?.length && !changes.actions_removed?.length) {
    return (
      <p className="text-[11px] text-gray-500 italic">
        No material deterministic change vs the baseline.
      </p>
    );
  }
  return (
    <div className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-[11px]">
          <span className="text-gray-400">{k}</span>
          <span className="font-mono text-gray-200">{v}</span>
        </div>
      ))}
      {changes.actions_added?.length > 0 && (
        <div className="text-[11px]">
          <span className="text-emerald-300">+ Added: </span>
          <span className="text-gray-300">{changes.actions_added.join(' · ')}</span>
        </div>
      )}
      {changes.actions_removed?.length > 0 && (
        <div className="text-[11px]">
          <span className="text-rose-300">− Removed: </span>
          <span className="text-gray-300">{changes.actions_removed.join(' · ')}</span>
        </div>
      )}
    </div>
  );
}

function TurnView({ turn }) {
  return (
    <div className="space-y-2" data-testid="cashflow-whatif-turn">
      <div className="rounded-lg bg-slate-800/60 border border-slate-700/60 px-3 py-2">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">You asked</p>
        <p className="text-xs text-gray-200">{turn.question}</p>
      </div>
      {turn.error ? (
        <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-xs text-rose-200">
          {turn.error}
        </div>
      ) : turn.loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400 px-1">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Computing deterministic what-if + CFO narration…
        </div>
      ) : turn.response ? (
        <div className="rounded-lg bg-slate-900/70 border border-slate-700/60 px-3 py-2.5 space-y-2">
          <div className="flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
            <p
              className="text-xs text-gray-100 leading-relaxed"
              data-testid="cashflow-whatif-answer"
            >
              {turn.response.answer}
            </p>
          </div>

          {turn.response.assumptions?.length > 0 && (
            <div className="border-t border-dashed border-slate-700/60 pt-2">
              <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Assumptions</p>
              <ul className="text-[11px] text-gray-300 space-y-0.5 list-disc pl-4">
                {turn.response.assumptions.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-t border-dashed border-slate-700/60 pt-2">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
              Deterministic changes
            </p>
            <DetChangesBlock changes={turn.response.deterministic_changes} />
          </div>

          {turn.response.warnings?.length > 0 && (
            <div className="border-t border-dashed border-slate-700/60 pt-2 flex items-start gap-1.5 text-[11px] text-amber-300">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              <ul className="space-y-0.5">
                {turn.response.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {turn.response.recommended_next_action && (
            <div className="border-t border-dashed border-slate-700/60 pt-2 flex items-start gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-300 mt-0.5 shrink-0" />
              <p
                className="text-[11px] text-emerald-200"
                data-testid="cashflow-whatif-next-action"
              >
                <strong>Next: </strong>
                {turn.response.recommended_next_action}
              </p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function CashflowWhatIfChat() {
  const [input, setInput] = useState('');
  const [turns, setTurns] = useState([]);
  const [pending, setPending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [turns.length]);

  const ask = useCallback(async (question, adjustments) => {
    const q = (question || '').trim();
    if (!q) return;
    const turnId = Date.now();
    setTurns((prev) => [
      ...prev,
      { id: turnId, question: q, loading: true, response: null, error: null },
    ]);
    setPending(true);
    try {
      const res = await axios.post(`${API}/cashflow/what-if`, {
        question: q,
        adjustments: adjustments || {},
      });
      setTurns((prev) =>
        prev.map((t) =>
          t.id === turnId ? { ...t, loading: false, response: res.data } : t
        )
      );
    } catch (err) {
      console.error('Cashflow what-if failed:', err);
      const detail = err?.response?.data?.detail || 'Unable to answer that question.';
      setTurns((prev) =>
        prev.map((t) => (t.id === turnId ? { ...t, loading: false, error: detail } : t))
      );
    } finally {
      setPending(false);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pending) return;
    const q = input.trim();
    if (!q) return;
    setInput('');
    ask(q, {});
  };

  const handleSuggestion = (s) => {
    if (pending) return;
    ask(s.question, s.adjustments);
  };

  return (
    <details
      className="rounded-xl border border-slate-700/60 bg-slate-900/50 overflow-hidden"
      data-testid="cashflow-whatif"
    >
      <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MessageCircleQuestion className="w-4 h-4 text-gray-300" />
          <p className="text-sm font-semibold text-white">CFO What-If Q&amp;A</p>
        </div>
        <span className="text-[11px] text-gray-400">Read-only · planner-anchored</span>
      </summary>

      <div className="px-4 pb-4 pt-1 space-y-3">
        <div className="flex flex-wrap gap-1.5" data-testid="cashflow-whatif-suggestions">
          {SUGGESTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => handleSuggestion(s)}
              disabled={pending}
              className="text-[11px] px-2.5 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-gray-200 border border-slate-700/80 disabled:opacity-50"
              data-testid={`cashflow-whatif-suggestion-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {turns.length > 0 && (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {turns.map((t) => (
              <TurnView key={t.id} turn={t} />
            ))}
            <div ref={bottomRef} />
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a what-if… e.g. What should I do this week?"
            disabled={pending}
            className="flex-1 rounded-md bg-slate-900/80 border border-slate-700/80 px-3 py-1.5 text-xs text-gray-100 placeholder-gray-500 disabled:opacity-60 focus:outline-none focus:border-amber-500/60"
            data-testid="cashflow-whatif-input"
          />
          <button
            type="submit"
            disabled={pending || !input.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-semibold disabled:opacity-50"
            data-testid="cashflow-whatif-submit"
          >
            {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Ask
          </button>
        </form>

        <p className="text-[10px] text-gray-500">
          Read-only. The AI explains the deterministic planner. It cannot apply moves
          or write balances.
        </p>
      </div>
    </details>
  );
}
